// frontend/src/contexts/ChatContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";
import { chatApi } from "../utils/api";
import socketService from "../services/socketService";
import notificationService from "../services/notificationService";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // ==================== STATE ====================
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [chatableUsers, setChatableUsers] = useState([]);

  // Phase 3: Socket state
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [onlineUsers, setOnlineUsers] = useState(new Map());

  // Phase 4: Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Phase 6: Group chat state
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);

  // ==================== REFS ====================
  const pollAbortRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const lastMessageTimeRef = useRef(null);
  const isPollingRef = useRef(false);
  const mountedRef = useRef(true);
  const typingTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const unreadPollIntervalRef = useRef(null);

  // ==================== SOCKET CONNECTION ====================

  const connectSocket = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      const token = sessionStorage.getItem("webvenly_auth_token");
      if (!token) {
        console.warn("No auth token for socket connection");
        return;
      }

      await socketService.connect(token);
      setIsSocketConnected(true);
      console.log("ðŸ”Œ Chat socket connected");
    } catch (err) {
      console.error("Socket connection failed:", err);
      setIsSocketConnected(false);
    }
  }, [isAuthenticated, user]);

  const disconnectSocket = useCallback(() => {
    socketService.disconnect();
    setIsSocketConnected(false);
    setTypingUsers(new Map());
    setOnlineUsers(new Map());
  }, []);

  // ==================== API CALLBACKS ====================

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await chatApi.getUnreadCount();
      if (mountedRef.current) {
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, [isAuthenticated]);

  const fetchConversations = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const response = await chatApi.getConversations();
      if (mountedRef.current) {
        setConversations(response.data.conversations || []);
      }
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
      if (mountedRef.current) {
        setError("Failed to load conversations");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  // ==================== PANEL CONTROLS ====================

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ==================== FETCH FUNCTIONS ====================

  const fetchMessages = useCallback(async (conversationId, before = null) => {
    if (!conversationId) return;

    try {
      setIsLoadingMessages(true);
      const params = { limit: 50 };
      if (before) params.before = before;

      const response = await chatApi.getMessages(conversationId, params);

      if (mountedRef.current) {
        if (before) {
          setMessages((prev) => [...response.data.messages, ...prev]);
        } else {
          setMessages(response.data.messages || []);
          if (response.data.messages?.length > 0) {
            const lastMsg =
              response.data.messages[response.data.messages.length - 1];
            lastMessageTimeRef.current = lastMsg.created_at;
          }
        }
        setHasMoreMessages(response.data.hasMore || false);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      if (mountedRef.current) {
        setError("Failed to load messages");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingMessages(false);
      }
    }
  }, []);

  const fetchGroupParticipants = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      setIsLoadingParticipants(true);
      const response = await chatApi.getGroupParticipants(conversationId);
      if (mountedRef.current) {
        setGroupParticipants(response.data.participants || []);
      }
    } catch (err) {
      console.error("Failed to fetch group participants:", err);
    } finally {
      if (mountedRef.current) {
        setIsLoadingParticipants(false);
      }
    }
  }, []);

  // ==================== SOCKET EVENT HANDLERS ====================

  const setupSocketListeners = useCallback(() => {
    if (!socketService.connected) return;

    const unsubNewMessage = socketService.onNewMessage((data) => {
      if (!mountedRef.current) return;

      if (activeConversation?.conversation_id === data.conversationId) {
        setMessages((prev) => {
          const exists = prev.some(
            (m) => m.message_id === data.message.message_id
          );
          if (exists) return prev;
          return [...prev, data.message];
        });
        lastMessageTimeRef.current = data.message.created_at;
      }

      fetchConversations();

      if (activeConversation?.conversation_id !== data.conversationId) {
        fetchUnreadCount();
        notificationService.notifyNewMessage(data.message, () => {
          openChat();
        });
      } else {
        if (!document.hasFocus()) {
          notificationService.playSound();
        }
      }
    });

    const unsubMessageUpdated = socketService.onMessageUpdated((data) => {
      if (!mountedRef.current) return;

      if (activeConversation?.conversation_id === data.conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.message_id === data.messageId
              ? {
                  ...msg,
                  content: data.newContent,
                  is_edited: true,
                  edited_at: data.editedAt,
                }
              : msg
          )
        );
      }
    });

    const unsubMessageRead = socketService.onMessageRead((data) => {
      if (!mountedRef.current) return;

      if (activeConversation?.conversation_id === data.conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.message_id === data.messageId
              ? { ...msg, read_status: "Read", read_at: data.readAt }
              : msg
          )
        );
      }
    });

    const unsubTyping = socketService.onTypingUpdate((data) => {
      if (!mountedRef.current) return;

      if (activeConversation?.conversation_id === data.conversationId) {
        setTypingUsers((prev) => {
          const updated = new Map(prev);
          if (data.isTyping) {
            updated.set(data.userId, {
              userId: data.userId,
              userName: data.userName,
              startedAt: Date.now(),
            });
          } else {
            updated.delete(data.userId);
          }
          return updated;
        });
      }
    });

    const unsubOnline = socketService.onUserOnline((data) => {
      if (!mountedRef.current) return;
      setOnlineUsers((prev) => {
        const updated = new Map(prev);
        updated.set(data.userId, { isOnline: true, userName: data.userName });
        return updated;
      });
    });

    const unsubOffline = socketService.onUserOffline((data) => {
      if (!mountedRef.current) return;
      setOnlineUsers((prev) => {
        const updated = new Map(prev);
        updated.set(data.userId, { isOnline: false, lastSeen: data.lastSeen });
        return updated;
      });
    });

    const unsubNotification = socketService.onMessageNotification((data) => {
      if (!mountedRef.current) return;
      // Fetch both conversations and unread count when receiving a notification
      // This ensures the conversation list updates and shows the new message at the top
      fetchConversations();
      fetchUnreadCount();
    });

    // Group-specific events
    const unsubGroupAdded = socketService.on?.("group:added", () => {
      if (!mountedRef.current) return;
      fetchConversations();
      fetchUnreadCount();
    });

    const unsubGroupRemoved = socketService.on?.("group:removed", (data) => {
      if (!mountedRef.current) return;
      if (activeConversation?.conversation_id === data.conversationId) {
        setActiveConversation(null);
        setMessages([]);
        setGroupParticipants([]);
      }
      fetchConversations();
    });

    const unsubMemberJoined = socketService.on?.(
      "group:member_joined",
      (data) => {
        if (!mountedRef.current) return;
        if (activeConversation?.conversation_id === data.conversationId) {
          fetchGroupParticipants(data.conversationId);
        }
      }
    );

    const unsubMemberLeft = socketService.on?.("group:member_left", (data) => {
      if (!mountedRef.current) return;
      if (activeConversation?.conversation_id === data.conversationId) {
        fetchGroupParticipants(data.conversationId);
      }
    });

    const unsubGroupUpdated = socketService.on?.(
      "group:settings_changed",
      (data) => {
        if (!mountedRef.current) return;
        setConversations((prev) =>
          prev.map((conv) =>
            conv.conversation_id === data.conversationId
              ? { ...conv, ...data.changes }
              : conv
          )
        );
        if (activeConversation?.conversation_id === data.conversationId) {
          setActiveConversation((prev) => ({ ...prev, ...data.changes }));
        }
      }
    );

    return () => {
      unsubNewMessage();
      unsubMessageUpdated();
      unsubMessageRead();
      unsubTyping();
      unsubOnline();
      unsubOffline();
      unsubNotification();
      if (typeof unsubGroupAdded === "function") unsubGroupAdded();
      if (typeof unsubGroupRemoved === "function") unsubGroupRemoved();
      if (typeof unsubMemberJoined === "function") unsubMemberJoined();
      if (typeof unsubMemberLeft === "function") unsubMemberLeft();
      if (typeof unsubGroupUpdated === "function") unsubGroupUpdated();
    };
  }, [
    activeConversation,
    fetchConversations,
    fetchUnreadCount,
    openChat,
    fetchGroupParticipants,
  ]);

  // ==================== MESSAGE CALLBACKS ====================

  const sendMessage = useCallback(
    async (recipientIdOrOptions, messageText, options = {}) => {
      if (!messageText?.trim() && !options.attachment) return null;

      try {
        // Build the request payload
        const payload = {
          message: messageText?.trim() || "",
          authorization_id: options.authorizationId || null,
          consumer_id: options.consumerId || null,
          attachment_id: options.attachment?.attachment_id || null,
        };

        // Check if sending to a group (conversationId passed) or DM (recipientId passed)
        if (options.conversationId) {
          // Group message - use conversation_id directly
          payload.conversation_id = options.conversationId;
        } else {
          // DM - use recipient_id
          payload.recipient_id = recipientIdOrOptions;
        }

        const response = await chatApi.sendMessage(payload);

        const { message, conversation_id } = response.data;

        if (activeConversation?.conversation_id === conversation_id) {
          setMessages((prev) => [...prev, message]);
          lastMessageTimeRef.current = message.created_at;
        }

        if (isSocketConnected) {
          socketService.messageSent(conversation_id, message);
          socketService.stopTyping(activeConversation?.conversation_id);
        }

        fetchConversations();
        return response.data;
      } catch (err) {
        console.error("Failed to send message:", err);
        throw err;
      }
    },
    [activeConversation, fetchConversations, isSocketConnected]
  );

  const editMessage = useCallback(
    async (messageId, newContent) => {
      if (!newContent?.trim()) return null;

      try {
        const response = await chatApi.editMessage(messageId, {
          message: newContent.trim(),
        });

        setMessages((prev) =>
          prev.map((msg) =>
            msg.message_id === messageId
              ? {
                  ...msg,
                  content: newContent.trim(),
                  is_edited: true,
                  edited_at: response.data.edited_at,
                }
              : msg
          )
        );

        if (isSocketConnected && activeConversation?.conversation_id) {
          socketService.messageEdited(
            activeConversation.conversation_id,
            messageId,
            newContent.trim()
          );
        }

        return response.data;
      } catch (err) {
        console.error("Failed to edit message:", err);
        throw err;
      }
    },
    [isSocketConnected, activeConversation]
  );

  const deleteMessage = useCallback(async (messageId) => {
    try {
      await chatApi.deleteMessage(messageId);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === messageId
            ? { ...msg, is_deleted: true, deleted_at: new Date().toISOString() }
            : msg
        )
      );
    } catch (err) {
      console.error("Failed to delete message:", err);
      throw err;
    }
  }, []);

  const getMessageHistory = useCallback(async (messageId) => {
    try {
      const response = await chatApi.getMessageHistory(messageId);
      return response.data;
    } catch (err) {
      console.error("Failed to get message history:", err);
      throw err;
    }
  }, []);

  const markConversationRead = useCallback(
    async (conversationId) => {
      if (!conversationId) return;

      try {
        await chatApi.markConversationRead(conversationId);
        setConversations((prev) =>
          prev.map((conv) =>
            conv.conversation_id === conversationId
              ? { ...conv, unread_count: 0 }
              : conv
          )
        );
        fetchUnreadCount();
      } catch (err) {
        console.error("Failed to mark conversation as read:", err);
      }
    },
    [fetchUnreadCount]
  );

  // ==================== CONVERSATION CALLBACKS ====================

  const startConversation = useCallback(
    async (recipientId, options = {}) => {
      try {
        const response = await chatApi.createConversation({
          recipient_id: recipientId,
          authorization_id: options.authorizationId || null,
          consumer_id: options.consumerId || null,
        });
        fetchConversations();
        return response.data;
      } catch (err) {
        console.error("Failed to start conversation:", err);
        throw err;
      }
    },
    [fetchConversations]
  );

  const openConversation = useCallback(
    async (conversationOrId, options = {}) => {
      let conversation = conversationOrId;
      let conversationId = null;

      if (typeof conversationOrId === "string") {
        conversationId = conversationOrId;
        conversation = conversations.find(
          (c) => c.conversation_id === conversationId
        );
        if (!conversation) {
          conversation = { conversation_id: conversationId };
        }
      } else {
        conversationId = conversationOrId?.conversation_id;
      }

      if (
        isSocketConnected &&
        activeConversation?.conversation_id &&
        activeConversation.conversation_id !== conversationId
      ) {
        socketService.leaveConversation(activeConversation.conversation_id);
      }

      setActiveConversation(conversation);
      setMessages([]);
      setTypingUsers(new Map());
      setGroupParticipants([]);
      lastMessageTimeRef.current = null;

      if (conversationId) {
        if (isSocketConnected) {
          socketService.joinConversation(conversationId);
        }

        await fetchMessages(conversationId);
        await markConversationRead(conversationId);

        if (conversation?.is_group) {
          fetchGroupParticipants(conversationId);
        }

        if (options.scrollToMessageId) {
          setTimeout(() => {
            const messageElement = document.getElementById(
              `message-${options.scrollToMessageId}`
            );
            if (messageElement) {
              messageElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              messageElement.classList.add("message-highlight");
              setTimeout(() => {
                messageElement.classList.remove("message-highlight");
              }, 2000);
            }
          }, 300);
        }
      }
    },
    [
      fetchMessages,
      markConversationRead,
      isSocketConnected,
      activeConversation,
      conversations,
      fetchGroupParticipants,
    ]
  );

  const closeConversation = useCallback(() => {
    if (isSocketConnected && activeConversation?.conversation_id) {
      socketService.leaveConversation(activeConversation.conversation_id);
    }

    setActiveConversation(null);
    setMessages([]);
    setTypingUsers(new Map());
    setGroupParticipants([]);
    lastMessageTimeRef.current = null;
  }, [isSocketConnected, activeConversation]);

  // ==================== GROUP CHAT CALLBACKS ====================

  const createGroup = useCallback(
    async (groupName, participantIds, options = {}) => {
      try {
        const response = await chatApi.createGroup({
          group_name: groupName,
          group_description: options.description || null,
          participant_ids: participantIds,
          authorization_id: options.authorizationId || null,
          consumer_id: options.consumerId || null,
        });

        if (isSocketConnected) {
          socketService.emit("group:created", {
            conversationId: response.data.conversation_id,
            participants: [user?.user_id, ...participantIds],
          });
        }

        fetchConversations();
        return response.data;
      } catch (err) {
        console.error("Failed to create group:", err);
        throw err;
      }
    },
    [fetchConversations, isSocketConnected, user?.user_id]
  );

  const updateGroup = useCallback(
    async (groupId, updates) => {
      try {
        const response = await chatApi.updateGroup(groupId, updates);

        setConversations((prev) =>
          prev.map((conv) =>
            conv.conversation_id === groupId
              ? { ...conv, ...response.data }
              : conv
          )
        );

        if (activeConversation?.conversation_id === groupId) {
          setActiveConversation((prev) => ({ ...prev, ...response.data }));
        }

        if (isSocketConnected) {
          socketService.emit("group:updated", {
            conversationId: groupId,
            changes: response.data,
          });
        }

        return response.data;
      } catch (err) {
        console.error("Failed to update group:", err);
        throw err;
      }
    },
    [activeConversation, isSocketConnected]
  );

  const addGroupParticipants = useCallback(
    async (groupId, participantIds) => {
      try {
        const response = await chatApi.addGroupParticipants(groupId, {
          participant_ids: participantIds,
        });

        if (activeConversation?.conversation_id === groupId) {
          fetchGroupParticipants(groupId);
        }

        return response.data;
      } catch (err) {
        console.error("Failed to add participants:", err);
        throw err;
      }
    },
    [activeConversation, fetchGroupParticipants]
  );

  const removeGroupParticipant = useCallback(
    async (groupId, userId, reason = null) => {
      try {
        const response = await chatApi.removeGroupParticipant(groupId, userId, {
          reason,
        });

        if (activeConversation?.conversation_id === groupId) {
          fetchGroupParticipants(groupId);
        }

        return response.data;
      } catch (err) {
        console.error("Failed to remove participant:", err);
        throw err;
      }
    },
    [activeConversation, fetchGroupParticipants]
  );

  const leaveGroup = useCallback(
    async (groupId) => {
      try {
        await chatApi.leaveGroup(groupId);

        if (activeConversation?.conversation_id === groupId) {
          closeConversation();
        }

        fetchConversations();
      } catch (err) {
        console.error("Failed to leave group:", err);
        throw err;
      }
    },
    [activeConversation, closeConversation, fetchConversations]
  );

  const updateParticipantRole = useCallback(
    async (groupId, userId, newRole) => {
      try {
        const response = await chatApi.updateGroupParticipantRole(
          groupId,
          userId,
          { new_role: newRole }
        );
        setGroupParticipants((prev) =>
          prev.map((p) => (p.user_id === userId ? { ...p, role: newRole } : p))
        );
        return response.data;
      } catch (err) {
        console.error("Failed to update participant role:", err);
        throw err;
      }
    },
    []
  );

  const toggleGroupMute = useCallback(async (groupId, muted) => {
    try {
      const response = await chatApi.toggleGroupMute(groupId, { muted });
      setConversations((prev) =>
        prev.map((conv) =>
          conv.conversation_id === groupId
            ? { ...conv, is_muted: response.data.is_muted }
            : conv
        )
      );
      return response.data;
    } catch (err) {
      console.error("Failed to toggle mute:", err);
      throw err;
    }
  }, []);

  // ==================== OTHER CALLBACKS ====================

  const startTyping = useCallback(() => {
    if (!activeConversation?.conversation_id || !isSocketConnected) return;

    socketService.startTyping(activeConversation.conversation_id);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(activeConversation.conversation_id);
    }, 3000);
  }, [activeConversation, isSocketConnected]);

  const stopTyping = useCallback(() => {
    if (!activeConversation?.conversation_id || !isSocketConnected) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    socketService.stopTyping(activeConversation.conversation_id);
  }, [activeConversation, isSocketConnected]);

  const isUserOnline = useCallback(
    (userId) => {
      const userData = onlineUsers.get(userId);
      return userData?.isOnline || false;
    },
    [onlineUsers]
  );

  const requestPresence = useCallback(
    (userIds) => {
      if (!isSocketConnected) return;
      socketService.requestPresence(userIds);
    },
    [isSocketConnected]
  );

  const requestNotificationPermission = useCallback(async () => {
    const granted = await notificationService.requestPermission();
    setNotificationsEnabled(granted);
    return granted;
  }, []);

  const getNotificationStatus = useCallback(() => {
    return notificationService.getStatus();
  }, []);

  const toggleNotificationSound = useCallback((enabled) => {
    notificationService.setSoundEnabled(enabled);
  }, []);

  const searchMessages = useCallback(
    async (query, options = {}) => {
      if (!isAuthenticated || !query?.trim()) {
        return { messages: [], total: 0, hasMore: false };
      }

      try {
        const params = {
          q: query.trim(),
          limit: options.limit || 20,
          offset: options.offset || 0,
        };

        if (options.conversationId) {
          params.conversation_id = options.conversationId;
        }

        const response = await chatApi.searchMessages(params);
        return {
          messages: response.data.messages || [],
          total: response.data.total || 0,
          hasMore: response.data.hasMore || false,
        };
      } catch (err) {
        console.error("Failed to search messages:", err);
        throw err;
      }
    },
    [isAuthenticated]
  );

  const fetchChatableUsers = useCallback(
    async (search = "") => {
      if (!isAuthenticated) return;

      try {
        const response = await chatApi.getUsers({ search });
        if (mountedRef.current) {
          setChatableUsers(response.data.users || []);
        }
        return response.data.users || [];
      } catch (err) {
        console.error("Failed to fetch chatable users:", err);
        return [];
      }
    },
    [isAuthenticated]
  );

  const loadMoreMessages = useCallback(async () => {
    if (!activeConversation || !hasMoreMessages || isLoadingMessages) return;

    const oldestMessage = messages[0];
    if (oldestMessage) {
      await fetchMessages(
        activeConversation.conversation_id,
        oldestMessage.created_at
      );
    }
  }, [
    activeConversation,
    hasMoreMessages,
    isLoadingMessages,
    messages,
    fetchMessages,
  ]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      connectSocket();
    } else {
      disconnectSocket();
    }
    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated, user, connectSocket, disconnectSocket]);

  useEffect(() => {
    if (isSocketConnected) {
      const cleanup = setupSocketListeners();
      return cleanup;
    }
  }, [isSocketConnected, setupSocketListeners]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      fetchConversations();
    } else {
      setConversations([]);
      setMessages([]);
      setActiveConversation(null);
      setUnreadCount(0);
      setGroupParticipants([]);
    }
  }, [isAuthenticated, fetchUnreadCount, fetchConversations]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (unreadPollIntervalRef.current) {
        clearInterval(unreadPollIntervalRef.current);
        unreadPollIntervalRef.current = null;
      }
      return;
    }

    const pollInterval = isSocketConnected ? 30000 : 10000;

    unreadPollIntervalRef.current = setInterval(() => {
      if (mountedRef.current && !isOpen) {
        fetchUnreadCount();
      }
    }, pollInterval);

    return () => {
      if (unreadPollIntervalRef.current) {
        clearInterval(unreadPollIntervalRef.current);
        unreadPollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, isSocketConnected, isOpen, fetchUnreadCount]);

  useEffect(() => {
    if (isSocketConnected) {
      heartbeatRef.current = setInterval(() => {
        socketService.sendHeartbeat();
      }, 30000);
    } else {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    }

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [isSocketConnected]);

  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => {
        const updated = new Map(prev);
        let changed = false;
        prev.forEach((data, key) => {
          if (now - data.startedAt > 6000) {
            updated.delete(key);
            changed = true;
          }
        });
        return changed ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(cleanup);
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (unreadPollIntervalRef.current) {
        clearInterval(unreadPollIntervalRef.current);
      }
      disconnectSocket();
    };
  }, [disconnectSocket]);

  useEffect(() => {
    const status = notificationService.getStatus();
    setNotificationsEnabled(status.isEnabled);
  }, []);

  // ==================== CONTEXT VALUE ====================

  const contextValue = useMemo(
    () => ({
      // State
      isOpen,
      conversations,
      activeConversation,
      messages,
      unreadCount,
      isLoading,
      isLoadingMessages,
      error,
      hasMoreMessages,
      chatableUsers,

      // Socket state
      isSocketConnected,
      typingUsers: Array.from(typingUsers.values()),
      onlineUsers,

      // Group state
      groupParticipants,
      isLoadingParticipants,

      // Actions
      toggleChat,
      openChat,
      closeChat,
      openConversation,
      closeConversation,
      sendMessage,
      editMessage,
      deleteMessage,
      getMessageHistory,
      markConversationRead,
      startConversation,
      fetchConversations,
      fetchChatableUsers,
      searchMessages,
      loadMoreMessages,
      fetchUnreadCount,

      // Group actions
      createGroup,
      updateGroup,
      addGroupParticipants,
      removeGroupParticipant,
      leaveGroup,
      updateParticipantRole,
      toggleGroupMute,
      fetchGroupParticipants,

      // Typing
      startTyping,
      stopTyping,

      // Presence
      isUserOnline,
      requestPresence,

      // Notifications
      notificationsEnabled,
      requestNotificationPermission,
      getNotificationStatus,
      toggleNotificationSound,

      // Current user info
      currentUserId: user?.user_id,
    }),
    [
      isOpen,
      conversations,
      activeConversation,
      messages,
      unreadCount,
      isLoading,
      isLoadingMessages,
      error,
      hasMoreMessages,
      chatableUsers,
      isSocketConnected,
      typingUsers,
      onlineUsers,
      groupParticipants,
      isLoadingParticipants,
      toggleChat,
      openChat,
      closeChat,
      openConversation,
      closeConversation,
      sendMessage,
      editMessage,
      deleteMessage,
      getMessageHistory,
      markConversationRead,
      startConversation,
      fetchConversations,
      fetchChatableUsers,
      searchMessages,
      loadMoreMessages,
      fetchUnreadCount,
      createGroup,
      updateGroup,
      addGroupParticipants,
      removeGroupParticipant,
      leaveGroup,
      updateParticipantRole,
      toggleGroupMute,
      fetchGroupParticipants,
      startTyping,
      stopTyping,
      isUserOnline,
      requestPresence,
      notificationsEnabled,
      requestNotificationPermission,
      getNotificationStatus,
      toggleNotificationSound,
      user?.user_id,
    ]
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export { ChatContext };
